import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type {Note} from '../types/Note';
import {subscribeNotes} from '../services/firestoreNotes';
import {useAuth} from './AuthProvider';

interface NotesContextValue {
  notes: Note[];
  loading: boolean;
}

const NotesContext = createContext<NotesContextValue>({
  notes: [],
  loading: true,
});

export function NotesProvider({children}: {children: ReactNode}) {
  const {uid} = useAuth();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setNotes([]);
      return;
    }
    setLoading(true);
    return subscribeNotes(
      uid,
      incoming => {
        setNotes(incoming);
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [uid]);

  const sorted = useMemo(
    () =>
      [...notes].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [notes],
  );

  return (
    <NotesContext.Provider value={{notes: sorted, loading}}>
      {children}
    </NotesContext.Provider>
  );
}

export const useNotes = () => useContext(NotesContext);
